import { ChangeEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Box,
  Button,
  Devider,
  FilterContext,
  Flex,
  InputSearch,
  PaginationPerPageSelect,
  SearchContext,
  Select,
  Text,
  UploadMediaContainer,
  ModalContext,
  Checkbox,
  MultiFileUploadContext,
  PageViewToggle,
  PageViewTypeEnum,
  MediaDragAndDropContext,
} from 'dash-ui';
import {
  MediaFileTypesFilterEnum,
  ProgressTrackingEvent,
  ProgressTrackingTypesEnum,
} from 'dash-common';

import { useUploadMediaFile } from 'common/hooks/useUploadMediaFile';
import { BulkActionsContext } from 'common/contexts/bulkActionsContext/bulkActionsContext';
import { BreadcrumbContext } from 'common/contexts/breadcrumb-context';
import {
  useAbortAllUploadsMutation,
  useAbortUploadFileMutation,
  useMoveMediaInSortOrderMutation,
  useObserveProgressTrackingByTypeSubscription,
} from 'gql/generated.graphql';
import { stickyTopBarStyles } from 'containers/properties/helpers/stickyTopBarStyles';

const SelectAllContainer = styled(Flex)`
  border-radius: ${({ theme }) => theme.space * 2}px;
  border: 1px solid ${({ theme }) => theme.colors.n40};
`;

const PageWrapper = styled(Box)<{ top?: number }>`
  ${stickyTopBarStyles}
`;

const MediaPageHeader = ({
  propertyId,
  folderId,
}: {
  propertyId: string;
  folderId?: string;
}) => {
  const { t } = useTranslation(['pages', 'common']);
  const { selected, setIsAllSelectedSpy, isAllSelected } =
    useContext(BulkActionsContext);
  const { setFilterState } = useContext(FilterContext);
  const { setSearchSlug } = useContext(SearchContext);
  const { toggleModal } = useContext(ModalContext);
  const [isOpenedUploadContainer, setIsOpenedUploadContainer] = useState(false);
  const uploadMediaFile = useUploadMediaFile();
  const { addProgressTrackingEvent } = useContext(MultiFileUploadContext);
  const [inputValue, setInputValue] = useState('');
  const { mediaIds, resetState } = useContext(MediaDragAndDropContext);
  const { isSticky } = useContext(BreadcrumbContext);

  const [abortUpload] = useAbortUploadFileMutation();
  const [abortAllUploads] = useAbortAllUploadsMutation();
  const [moveMediaInSortOrder] = useMoveMediaInSortOrderMutation();

  useObserveProgressTrackingByTypeSubscription({
    variables: { type: ProgressTrackingTypesEnum.FILE_UPLOAD },
    onData({ data }) {
      const incomingProgressTrackingEvent =
        data?.data?.observeProgressTrackingByType.document;

      if (incomingProgressTrackingEvent) {
        addProgressTrackingEvent(
          incomingProgressTrackingEvent as ProgressTrackingEvent
        );
      }
    },
  });

  const setMediaSortOrder = () => {
    moveMediaInSortOrder({
      variables: {
        sortedMediaIds: mediaIds,
      },
      onCompleted: () => {
        resetState();
      },
    });
  };

  return (
    <PageWrapper w="100%" pos="relative">
      <Flex px={6} py={4} alignItems="center" justifyContent="space-between">
        <Text lg>{t('pages:propertyPage.mediaFiles')}</Text>
        <Flex alignItems="center">
          <Button
            mr={4}
            px={10.5}
            onClick={setMediaSortOrder}
            disabled={!mediaIds.length}
          >
            {t('common:labels.save')}
          </Button>
          <PageViewToggle
            defaultButton={PageViewTypeEnum.LIST}
            buttons={[
              { icon: 'apps', viewType: PageViewTypeEnum.CARDS },
              { icon: 'list', viewType: PageViewTypeEnum.LIST },
            ]}
          />
        </Flex>
      </Flex>
      <Devider />
      <Flex py={4} px={6} alignItems="center">
        <SelectAllContainer minWidth={122} mr={3} p={2}>
          <Flex>
            <Checkbox
              checked={isAllSelected}
              mr={2}
              onChange={(checked) => setIsAllSelectedSpy(checked)}
            />
          </Flex>
          <Flex>
            <Text>{t('common:labels:selectAll')}</Text>
          </Flex>
        </SelectAllContainer>
        <InputSearch
          w="100%"
          value={inputValue}
          placeholder={t('pages:propertyPage.searchMedia') || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchSlug(e.target.value);
            setInputValue(e.target.value);
          }}
          onClear={() => {
            setSearchSlug('');
            setInputValue('');
          }}
        />
        <Flex alignItems="center">
          <PaginationPerPageSelect
            w={130}
            mx={4}
            label={t('common:labels.show') || ''}
            options={[20, 40, 60, 100]}
            defaultValue={40}
            menuPosition={isSticky ? 'fixed' : 'absolute'}
          />
          <Select
            menuPosition={isSticky ? 'fixed' : 'absolute'}
            w={144}
            mr={4}
            isMulti
            onChanged={(_: string, values?: string[]) =>
              setFilterState('fileTypes', values as MediaFileTypesFilterEnum[])
            }
            placeholder={t('pages:propertyPage.allFileTypes')}
            options={Object.values(MediaFileTypesFilterEnum).map((item) => ({
              label: item,
              value: item,
            }))}
          />
          <Button
            onClick={() => toggleModal(true, 'SetFolderNameModal')}
            variation="primary"
            icon="plus"
            mr={4}
            w={119}
          >
            {t('common:labels.folder')}
          </Button>
          <Button
            w={119}
            variation="primary"
            icon="plus"
            onClick={() => setIsOpenedUploadContainer(true)}
          >
            {t('common:labels.file')}
          </Button>
        </Flex>
      </Flex>
      <Devider />

      <UploadMediaContainer
        mb={selected.length > 0 ? 20 : 0}
        visible={isOpenedUploadContainer}
        onClose={() => setIsOpenedUploadContainer(false)}
        cancelAllUploads={abortAllUploads}
        cancelUpload={(trackingId: string = '') =>
          abortUpload({
            variables: { progressTrackingId: trackingId },
          })
        }
        onUpload={(filesToUpload) => {
          filesToUpload.forEach((fileUpload) => {
            uploadMediaFile({
              fileUpload,
              propertyId,
              mediaFolderId: folderId,
            });
          });
        }}
      />
    </PageWrapper>
  );
};

export default MediaPageHeader;
